import {
  CookieConsentCategory,
  CookieConsentManager,
  CookieConsent as CookieConsentNamespace,
  DisplayMode,
} from '@almacareer/cookie-consent-manager';
import {
  clearLocalStorageFromNonArnoldItems,
  CookieConsentStyle,
  eraseAllCookies,
  getFrontendConfigValue,
  getHostedFrontendConfigValue,
} from '@arnold/common';
import { ENVIRONMENT } from '@arnold/core';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { allowedLanguages } from '../lib/common';

const environment = getFrontendConfigValue('ENVIRONMENT');

export let cookieConsentManager: typeof CookieConsentNamespace;

export default function CookieConsent() {
  const { i18n, t } = useTranslation('cookieConsent');

  useEffect(() => {
    if (![ENVIRONMENT.DEV, ENVIRONMENT.DEPLOY, ENVIRONMENT.PROD].includes(environment)) {
      return;
    }

    // What the hell does this do? And why is it here?
    const cookieBannerWrapper = document.getElementById('cc_div');
    if (cookieBannerWrapper) {
      cookieBannerWrapper.remove();
    }

    if (cookieConsentManager) {
      cookieConsentManager.setLanguage(i18n.language);
    } else {
      const handleConsent = () => {
        if (!cookieConsentManager!.acceptedCategory(CookieConsentCategory.ANALYTICS)) {
          eraseAllCookies(environment !== ENVIRONMENT.LOCAL);
          clearLocalStorageFromNonArnoldItems();
        }
      };

      // Generate cookieTable for all allowed languages
      const cookieTable: Record<string, any> = {};

      allowedLanguages.forEach((lang) => {
        cookieTable[lang] = {
          necessary: [
            {
              name: 'almacareer_ccm',
              description: t('necessary-almacareer_ccm', { lng: lang }),
              expiration: t('expiration-year', { count: 1, postProcess: 'interval', lng: lang }),
            },
            {
              name: 'intercomHash',
              description: t('necessary-intercomHash', { lng: lang }),
              expiration: t('expiration-day', { count: 60, postProcess: 'interval', lng: lang }),
            },
            {
              name: 'i18nextLng',
              description: t('necessary-i18nextLng', { lng: lang }),
              expiration: t('expiration-year', { count: 1, postProcess: 'interval', lng: lang }),
            },
            {
              name: '@Arnold',
              description: t('necessary-@Arnold', { lng: lang }),
              expiration: t('expiration-year', { count: 1, postProcess: 'interval', lng: lang }),
            },
            {
              name: 'accessToken',
              description: t('necessary-accessToken', { lng: lang }),
              expiration: t('expiration-session', { lng: lang }),
            },
            {
              name: 'deviceToken',
              description: t('necessary-deviceToken', { lng: lang }),
              expiration: t('expiration-day', { count: 60, postProcess: 'interval', lng: lang }),
            },
            {
              name: 'userId',
              description: t('necessary-userId', { lng: lang }),
              expiration: t('expiration-day', { count: 60, postProcess: 'interval', lng: lang }),
            },
            {
              name: 'userEmail',
              description: t('necessary-userEmail', { lng: lang }),
              expiration: t('expiration-day', { count: 60, postProcess: 'interval', lng: lang }),
            },
            {
              name: 'sso_session',
              description: t('necessary-sso_session', { lng: lang }),
              expiration: t('expiration-day', { count: 60, postProcess: 'interval', lng: lang }),
            },
          ],
          analytics: [
            {
              name: '_ga',
              description: t('analytics-ga', { lng: lang }),
              expiration: t('expiration-year', { count: 3, postProcess: 'interval', lng: lang }),
            },
            {
              name: '_gcl',
              description: t('analytics-gcl', { lng: lang }),
              expiration: t('expiration-day', { count: 90, postProcess: 'interval', lng: lang }),
            },
            {
              name: 'udid',
              description: t('analytics-udid', { lng: lang }),
              expiration: t('expiration-month', { count: 2, postProcess: 'interval', lng: lang }),
            },
            {
              name: '__eoi',
              description: t('analytics-eoi', { lng: lang }),
              expiration: t('expiration-day', { count: 90, postProcess: 'interval', lng: lang }),
            },
            {
              name: '__cf_bm',
              description: t('analytics-cf_bm', { lng: lang }),
              expiration: t('expiration-session', { lng: lang }),
            },
          ],
          ad: [
            {
              name: 'leady_session_id',
              description: t('ad-leady_session_id', { lng: lang }),
              expiration: t('expiration-session', { lng: lang }),
            },
          ],
        };
      });

      cookieConsentManager = CookieConsentManager(getFrontendConfigValue('SELF_CARE_URL'), {
        defaultLang: i18n.language,
        // @ts-ignore
        displayMode: DisplayMode.FORCE,
        autodetectLang: false,
        config: {
          cookie: {
            domain: getHostedFrontendConfigValue('COOKIE_DOMAIN'),
          },
        },
        onFirstConsent: handleConsent,
        onChange: handleConsent,
        onConsent: handleConsent,
        cookieTable,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  return <CookieConsentStyle />;
}
